<template>
  <div>
    <div class="survey-header title-actions title-actions--spacing">
      <h1 class="no-margin">{{ surveyResponse.survey_title }}</h1>
      <div class="note">Survey Ref: {{ surveyRef }}</div>
    </div>
    <steps :items="pages" :active-item="activePage" :latest-item="nextPage" :click-event="goToPage"
      v-if="pages.length > 1"></steps>
    <vue-form-generator :schema="formSchema" :model="formModel" :options="formOptions" @validated="onValidated"
      ref="vfg" />
    <div class="container--small block-center" v-if="branding === 'rrelentless'">
      <p class="x-small text-center spacing">
        <strong>Survey provided by <a href="https://www.rradar.com">rradar</a></strong>
      </p>
      <p class="x-small text-center spacing">
        rrelentless Limited is an appointed representative of Geo Underwriting Services Limited, which is Authorised and
        Regulated by the Financial Conduct Authority (FCA register Number 308400). rrelentless Limited is registered in
        England (no.13098017), registered address: 6 Beacon Way, Hull, England, HU3 4AE
      </p>
    </div>
    <div class="container--small block-center" v-else-if="branding">
      <p class="x-small text-center spacing">
        rradar is a UK legal practice which operates through separately constituted and regulated legal entities which
        provide clients with legal and other services pursuant to the law and regulations which govern their
        respective
        legal jurisdictions. Details of those different rradar corporate entities can be found in our Legal Notices.
      </p>
      <p class="x-small text-center">
        rradar Limited is registered in England (No. 07738271). Authorised and Regulated by the Solicitors Regulation
        Authority SRA No. 608114
        <br>VAT Registration No. GB 214 672 420
        <br>Registered office 6 Beacon Way, Hull, HU3 4AE
        <br>FCA registration number LS 608114.
      </p>
      <p class="x-small text-center">
        rradar (Scotland) Limited is registered in Scotland (No. SC519039). Recognised and Regulated by the Law
        Society
        of Scotland (No. 50480)
        <br>Registered office 145 St Vincent Street, Glasgow, GJ 5JF
        <br>FCA registration number LSS 50480.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import Common from "@/store/common";
import Steps from "@/components/Steps";
import formBuilder from "@/mixins/form-builder";

export default {
  name: "new-survey",

  props: [
    "surveyRef",
    "surveyId",
    "surveyInviteId",
    "hasLastResponse",
    "pageId",
  ],

  components: { steps: Steps },

  mixins: [formBuilder],

  data: () => {
    return {
      useLastResponse: false,
      startedOrResumed: false,
    };
  },

  computed: {
    ...mapState({
      surveyResponse: (state) => state.survey.surveyResponse,
      pages: (state) => state.survey.pages,
      submissionAttempted: (state) => state.survey.submissionAttempted,
      surveysLoaded: (state) => state.survey.surveysLoaded,
      surveyLoaded: (state) => state.survey.surveyLoaded,
      willAuthenticate: (state) => state.user.willAuthenticate,
    }),

    ...mapGetters([
      "activePage",
      "previousPage",
      "nextPage",
      "incompleteSurveys",
      "isAuthenticated",
      "branding"
    ]),

    resumeSurveyPage() {
      return (
        (this.surveyId && this.pageId) ||
        (this.incompleteSurvey &&
          this.incompleteSurvey.response &&
          !this.incompleteSurvey.response.complete)
      );
    },

    incompleteSurvey() {
      return this.incompleteSurveys.find(
        (survey) => survey.reference === this.surveyRef
      );
    },
  },

  methods: {
    ...mapMutations(["setActivePage"]),

    ...mapActions([
      "startSurvey",
      "resumeSurvey",
      "fetchSurveyResponse",
      "fetchPages",
      "renderPage",
    ]),

    onValidated(isValid) {
      if (!isValid && this.submissionAttempted) {
        setTimeout(() => {
          window.scrollTo(
            0,
            document.querySelector(".form-group.error").offsetTop
          );
        }, 0);
      }
    },

    goToPage(page) {
      if (page.position > this.nextPage.position) return;
      if (this.formModelChanged) {
        Common.confirmDialog({
          modal: this.$modal,
          title: "You have unsaved changes. Do you want to save them?",
          okText: "Yes",
          cancelText: "No",
          okHandler: async () => {
            this.handleSubmit(
              this.formModel,
              undefined,
              undefined,
              this.$refs.vfg,
              true,
              true
            ).then(() => this.setPage(page));
          },
          cancelHandler: () => {
            this.setPage(page);
          },
        });
      } else {
        this.setPage(page);
      }
    },

    setPage(page) {
      if (this.resumeSurveyPage) {
        this.$router.push({
          name: "survey-resume",
          params: {
            surveyId: this.surveyId || this.incompleteSurvey.response.id,
            pageId: page.id,
          },
        });
      } else {
        this.setActivePage(page.id);
      }
    },

    setPageResponse(page = this.activePage.id) {
      const pageResponse =
        this.surveyResponse.response_data.pages &&
        this.surveyResponse.response_data.pages.find(
          (p) => p.page.id === parseInt(page)
        );
      if (pageResponse !== undefined) {
        const model = { dataRefs: {} };
        Object.keys(pageResponse.data).forEach((field) => {
          this.$set(
            model,
            field,
            this.convertDataTextToValues(field, pageResponse.data[field])
          );
          model.dataRefs[field] = pageResponse.data[field].data_refs;
        });
        this.formModel = model;
      }
    },

    convertDataTextToValues(model, field) {
      const schemaField = this.formSchema.fields.find((f) => f.model === model);

      if (!schemaField || schemaField.values.length === 0) return field.data;

      let value;
      if (Array.isArray(field.data) && field.data_refs !== undefined) {
        const schemaValues = schemaField.values.filter((v) =>
          field.data_refs.includes(v.ref)
        );

        value = schemaValues.map((v) =>
          this.setSchemaValue(v, field.data[field.data_refs.indexOf(v.ref)])
        );
      } else if (
        typeof field.data === "string" &&
        field.data_refs !== undefined
      ) {
        const schemaValue = schemaField.values.find((v) =>
          field.data_refs.includes(v.ref)
        );

        value = this.setSchemaValue(schemaValue, field.data);
      } else {
        value = field.data;
      }
      return value;
    },

    setSchemaValue(schemaValue, data) {
      const inputBoxText = data && data.replace(/.*:\s/, "");

      if (schemaValue.hasInputBox) {
        this.$set(schemaValue, "value", inputBoxText);
      }

      return schemaValue.hasInputBox
        ? `${schemaValue.originalValue}: ${inputBoxText}`
        : schemaValue.value;
    },

    callStartSurvey(surveyInviteId) {
      this.startSurvey({
        survey: this.surveyRef,
        surveyInvite: this.surveyInviteId || surveyInviteId,
        useLastResponse: this.useLastResponse,
      }).then(() => this.$once("formBuilt", () => this.setPageResponse()));
    },

    startOrResumeSurvey() {
      if (this.resumeSurveyPage) {
        this.resumeSurvey({
          survey: this.surveyRef,
          surveyResponse: this.surveyId || this.incompleteSurvey.response.id,
          page: this.pageId || this.incompleteSurvey.response.next_page,
          authId: this.surveyResponse.auth_id,
        });
        this.$once("formBuilt", () => this.setPageResponse(this.pageId));
      } else if (
        (this.surveyInviteId && this.hasLastResponse !== false) ||
        (this.incompleteSurvey &&
          this.incompleteSurvey.response &&
          this.incompleteSurvey.response.complete)
      ) {
        const surveyInviteId = this.incompleteSurvey
          ? this.incompleteSurvey.invite_id
          : this.surveyInviteId;
        Common.confirmDialog({
          modal: this.$modal,
          title:
            "Do you want to use your last response to this survey as a starting point?",
          okText: "Yes",
          cancelText: "No",
          okHandler: () => {
            this.useLastResponse = true;
            this.callStartSurvey(surveyInviteId);
          },
          cancelHandler: () => {
            this.callStartSurvey(surveyInviteId);
          },
        });
      } else {
        this.callStartSurvey();
      }
      this.startedOrResumed = true;
    },
  },

  watch: {
    pageId(newPageId) {
      this.setActivePage(newPageId);
      this.resumeSurvey({
        survey: this.surveyRef,
        surveyResponse: this.surveyId,
        page: newPageId,
        authId: this.surveyResponse.auth_id,
      });
      this.$once("formBuilt", () => this.setPageResponse());
    },

    surveysLoaded() {
      if (!this.startedOrResumed) this.startOrResumeSurvey();
    },

    surveyLoaded() {
      if (!this.startedOrResumed) this.startOrResumeSurvey();
    },
  },

  created() {
    if (this.surveysLoaded || this.surveyLoaded || (!this.willAuthenticate && !this.isAuthenticated))
      this.startOrResumeSurvey();
  },
};
</script>
